import { useEffect, useMemo } from 'react';

import { Edge, Node, Position, ReactFlow } from 'reactflow';

import 'reactflow/dist/style.css';
import { CustomCampaignSteps } from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { CampaignStateTypes } from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import EndNode, { Type } from 'domains/campaigns/components/Graph/EndNode';
import { NodeType as GraphNodeEnumType } from 'domains/campaigns/components/Graph/types';
import useVerticallyCenterNode from 'domains/campaigns/components/Graph/useVerticallyCenterNode';
import { CampaignChannels, CampaignGraph, EmailSendInputState, SplitTypes } from 'domains/campaigns/types';
import buildCampaignGraph from 'domains/campaigns/utils/buildCampaignGraph';
import theme from 'theme';

import CustomFlowNode, { CustomFlowNodeData } from './CustomFlowNode';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CUSTOM_FLOW_NODE = 'CUSTOM_FLOW_NODE';

const SPACING = 8;

const nodeTypes = {
    [GraphNodeEnumType.END_NODE]: EndNode,
    [CUSTOM_FLOW_NODE]: CustomFlowNode,
};

const edgeStyle = {
    animated: false,
    style: {
        stroke: theme.palette.action.active,
        strokeWidth: 2,
        strokeDasharray: 2,
    },
};

const splitEdgeLabelStyle = {
    labelBgBorderRadius: 4,
    labelShowBg: true,
    labelBgStyle: {
        fill: 'white',
        fillOpacity: 1,
        stroke: theme.palette.action.active,
        strokeWidth: 1,
    },
    labelStyle: {
        ...theme.typography.overline,
        fill: theme.palette.action.active,
    },
};

// ---------------------------------------------------------------------------
// Positioning helpers
// ---------------------------------------------------------------------------

type NodeBranch = 'spine' | 'meets' | 'dnm';

/**
 * Determines which column each graph node belongs to by propagating branch
 * membership from split-edge labels and splitType fields to all descendants.
 *
 * - 'spine'  → main column (x = X_SPINE)
 * - 'meets'  → left column  (x = X_MEETS)
 * - 'dnm'    → main column  (x = X_SPINE), sits below split in the same column
 *
 * This replicates exactly what LegacyCampaignNavBar does with `isSplitMeets`.
 */
const computeNodeBranches = ({ nodes, edges }: CampaignGraph): Map<string, NodeBranch> => {
    const branches = new Map<string, NodeBranch>();
    nodes.forEach((n) => branches.set(n.id, 'spine'));

    // Seed branch roots from edge labels and node splitType
    edges.forEach((e) => {
        if (e.label === 'meets') branches.set(e.target, 'meets');
        if (e.label === 'does_not_meet') branches.set(e.target, 'dnm');
    });
    nodes.forEach((n) => {
        if (n.data?.splitType === SplitTypes.MEETS) branches.set(n.id, 'meets');
        if (n.data?.splitType === SplitTypes.DOES_NOT_MEET) branches.set(n.id, 'dnm');
    });

    // Propagate branch to all descendants (fixed-point iteration)
    let changed = true;
    while (changed) {
        changed = false;
        for (const e of edges) {
            const src = branches.get(e.source);
            if (src !== 'spine' && branches.get(e.target) === 'spine') {
                branches.set(e.target, src);
                changed = true;
            }
        }
    }

    return branches;
};

// X_SPINE is wide enough so the MEETS column (X=0) and the spine/DNM column
// do not overlap, preventing edge paths from crossing active node areas.
const X_SPINE = SPACING * 40; // 320
const X_MEETS = 0;
// When a meets branch is present, place the shared end node at the midpoint
// so both terminal arcs travel through the empty gap between columns and
// never cross any active node on the other side.
const X_END_SPLIT = Math.round((X_MEETS + X_SPINE) / 2); // 160

const getX = (nodeId: string, branches: Map<string, NodeBranch>): number =>
    branches.get(nodeId) === 'meets' ? X_MEETS : X_SPINE;

const getY = (index: number): number => SPACING * (SPACING * 1.25 + index * 16);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Props = {
    activeSendIndex: number;
    activeStep: CustomCampaignSteps;
    campaignState: CampaignStateTypes;
    onRemoveClick: (index: number) => void;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GraphCampaignNavigation = ({
    activeSendIndex,
    activeStep,
    campaignState,
    onRemoveClick,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const graph = useMemo(() => buildCampaignGraph(campaignState.sends, undefined, campaignState.abTestConfig), [
        campaignState.sends,
        campaignState.abTestConfig,
    ]);

    // Determine if the last send is removable (same logic as before)
    const lastSendIndex = campaignState.sends.length - 1;
    const lastSend = campaignState.sends[lastSendIndex];
    const lastSendSplitType =
        lastSend && 'splitType' in lastSend ? (lastSend as EmailSendInputState).splitType : undefined;
    const canRemoveLastSend =
        lastSendIndex > 0 &&
        lastSend?.channel === CampaignChannels.EMAIL &&
        !lastSendSplitType &&
        !campaignState.abTestConfig;

    // Shared branch map — used by both nodes and edges useMemos so terminal edges
    // can be routed to the correct per-branch end node without recomputing.
    const branches = useMemo(() => computeNodeBranches(graph), [graph]);

    // -----------------------------------------------------------------------
    // Build ReactFlow nodes
    // -----------------------------------------------------------------------
    const nodes: Node<any>[] = useMemo(() => {
        const startNode: Node<any> = {
            id: 'flow-start',
            connectable: false,
            type: GraphNodeEnumType.END_NODE,
            data: { label: 'start', type: Type.START },
            position: { x: X_SPINE, y: 0 },
            sourcePosition: Position.Bottom,
        };

        const navigationNodes: Node<CustomFlowNodeData>[] = graph.nodes.map((node, index) => {
            const canRemove = node.type === 'send' && node.data?.sendIndex === lastSendIndex && canRemoveLastSend;

            return {
                id: node.id,
                connectable: false,
                type: CUSTOM_FLOW_NODE,
                data: {
                    ...node.data,
                    nodeType: node.type,
                    activeSendIndex,
                    activeStep,
                    readonly: false,
                    setActiveSendIndex,
                    setActiveStep,
                    canRemove,
                    onRemoveClick,
                },
                position: { x: getX(node.id, branches), y: getY(index) },
                sourcePosition: Position.Bottom,
                // All nodes receive edges from the top so routing stays vertical
                // within each column. Previously DNM nodes used Position.Right which
                // created a wide C-curve that crossed into the MEETS column area.
                targetPosition: Position.Top,
            };
        });

        // When a meets branch is present, centre the shared end node between
        // the two columns (X_END_SPLIT = 160). The meets terminal then arcs
        // rightward through the empty gap (X 0→160) and the spine terminal
        // arcs leftward through the gap (X 320→160) — neither path reaches
        // the other branch's active-node X range.
        // For linear campaigns with no meets branch the end stays in the spine
        // column so the single terminal goes straight down.
        const sourcesWithChildrenSet = new Set(graph.edges.map((e) => e.source));
        const hasMeetsTerminal = graph.nodes.some(
            (n) => !sourcesWithChildrenSet.has(n.id) && branches.get(n.id) === 'meets'
        );
        const endNode: Node<any> = {
            id: 'flow-end',
            connectable: false,
            type: GraphNodeEnumType.END_NODE,
            data: { label: 'end', type: Type.END },
            position: { x: hasMeetsTerminal ? X_END_SPLIT : X_SPINE, y: getY(graph.nodes.length) },
            targetPosition: Position.Top,
        };

        return [startNode, ...navigationNodes, endNode];
    }, [
        branches,
        graph.nodes,
        graph.edges,
        activeSendIndex,
        activeStep,
        setActiveSendIndex,
        setActiveStep,
        lastSendIndex,
        canRemoveLastSend,
        onRemoveClick,
    ]);

    // -----------------------------------------------------------------------
    // Build ReactFlow edges
    // -----------------------------------------------------------------------
    const edges: Edge<any>[] = useMemo(() => {
        const firstNodeId = graph.nodes[0]?.id;

        const edgeStart: Edge<any> = {
            ...edgeStyle,
            id: 'e-flow-start',
            source: 'flow-start',
            target: firstNodeId ?? 'flow-end',
            type: 'smoothstep',
            pathOptions: { borderRadius: 24 },
        };

        const navigationEdges: Edge<any>[] = graph.edges.map((edge) => {
            if (edge.label === 'meets') {
                return {
                    ...edgeStyle,
                    ...splitEdgeLabelStyle,
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    type: 'smoothstep',
                    label: 'meets',
                };
            }
            if (edge.label === 'does_not_meet') {
                return {
                    ...edgeStyle,
                    ...splitEdgeLabelStyle,
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    type: 'smoothstep',
                    label: 'does not meet',
                    // No pathOptions offset — with Position.Top on all targets the
                    // does-not-meet edge routes straight down within the spine column.
                };
            }
            return {
                ...edgeStyle,
                id: edge.id,
                source: edge.source,
                target: edge.target,
                type: 'default',
            };
        });

        // All terminal nodes connect to the single shared end node. The meets
        // terminal arcs through the empty gap between columns at the bottom.
        const sourcesWithChildren = new Set(graph.edges.map((e) => e.source));
        const terminalEdges: Edge<any>[] = graph.nodes
            .filter((n) => !sourcesWithChildren.has(n.id))
            .map((n) => ({
                ...edgeStyle,
                id: `e-${n.id}-flow-end`,
                source: n.id,
                target: 'flow-end',
                type: 'smoothstep',
                pathOptions: { borderRadius: 24 },
            }));

        return [edgeStart, ...navigationEdges, ...terminalEdges];
    }, [graph.nodes, graph.edges]);

    // -----------------------------------------------------------------------
    // Center active node when activeSendIndex or activeStep changes
    // -----------------------------------------------------------------------
    const centerNodeVertically = useVerticallyCenterNode();
    useEffect(() => {
        const activeNode = nodes.find((n) => n.type === CUSTOM_FLOW_NODE && n.data?.sendIndex === activeSendIndex);
        if (activeNode) {
            centerNodeVertically(activeNode.id);
        }
    }, [activeSendIndex, activeStep, nodes, centerNodeVertically]);

    return (
        <div
            data-testid="graph-campaign-navigation"
            role="navigation"
            aria-label="campaign workflow steps"
            style={{ height: '100%', width: '100%' }}
        >
            <ReactFlow
                nodeTypes={nodeTypes}
                nodes={nodes}
                edges={edges}
                maxZoom={1}
                minZoom={0.125}
                onInit={(instance) => instance.fitView({ duration: 500 })}
            />
        </div>
    );
};

export default GraphCampaignNavigation;
